import { Task, ExtendedTaskFilter } from '../types/Task';

/**
 * Utility functions for filtering tasks with the enhanced filter system
 */

export const filterTasks = (tasks: Task[], filter: ExtendedTaskFilter): Task[] => {
  return tasks.filter(task => {
    // Global search
    if (filter.search) {
      const searchTerm = filter.search.toLowerCase();
      const searchableText = [
        task.summary,
        task.description,
        task.tag,
        task.owner,
        task.reportedBy,
        task.videoTutorialTitle,
        ...task.labels
      ].join(' ').toLowerCase();
      
      if (!searchableText.includes(searchTerm)) {
        return false;
      }
    }

    // Basic filters
    if (filter.status && task.status !== filter.status) return false;
    if (filter.priority && task.priority !== filter.priority) return false;
    if (filter.owner && !task.owner.toLowerCase().includes(filter.owner.toLowerCase())) return false;
    if (filter.tag && !task.tag.toLowerCase().includes(filter.tag.toLowerCase())) return false;
    if (filter.label && !task.labels.some(label => 
      label.toLowerCase().includes(filter.label!.toLowerCase())
    )) return false;

    // Advanced filters
    if (filter.reportedBy && !task.reportedBy.toLowerCase().includes(filter.reportedBy.toLowerCase())) return false;

    // Date range filters
    if (filter.createdDateFrom && new Date(task.createdAt) < new Date(filter.createdDateFrom)) return false;
    if (filter.createdDateTo && new Date(task.createdAt) > new Date(filter.createdDateTo)) return false;
    if (filter.expectedDateFrom && new Date(task.expectedDate) < new Date(filter.expectedDateFrom)) return false;
    if (filter.expectedDateTo && new Date(task.expectedDate) > new Date(filter.expectedDateTo)) return false;
    if (filter.reportedDateFrom && new Date(task.reportedDate) < new Date(filter.reportedDateFrom)) return false;
    if (filter.reportedDateTo && new Date(task.reportedDate) > new Date(filter.reportedDateTo)) return false;

    // Time estimate filters
    if (filter.originalEstimateMin !== undefined && task.originalEstimate < filter.originalEstimateMin) return false;
    if (filter.originalEstimateMax !== undefined && task.originalEstimate > filter.originalEstimateMax) return false;
    if (filter.actualTimeMin !== undefined && task.actualTime < filter.actualTimeMin) return false;
    if (filter.actualTimeMax !== undefined && task.actualTime > filter.actualTimeMax) return false;

    // Quick filters
    if (filter.isOverdue && !isTaskOverdue(task)) return false;
    if (filter.isDueToday && !isTaskDueToday(task)) return false;
    if (filter.isDueThisWeek && !isTaskDueThisWeek(task)) return false;
    if (filter.hasVideoTutorial && !task.videoTutorialLink) return false;

    // Multi-select filters
    if (filter.priorities && filter.priorities.length > 0 && !filter.priorities.includes(task.priority)) return false;
    if (filter.labels && filter.labels.length > 0 && !filter.labels.some(label => 
      task.labels.includes(label)
    )) return false;

    return true;
  });
};

export const sortTasks = (tasks: Task[], filter: ExtendedTaskFilter): Task[] => {
  if (!filter.sortBy) return tasks;

  const sortedTasks = [...tasks];
  const order = filter.sortOrder || 'desc';

  sortedTasks.sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (filter.sortBy) {
      case 'createdAt':
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case 'expectedDate':
        aValue = new Date(a.expectedDate);
        bValue = new Date(b.expectedDate);
        break;
      case 'priority':
        const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
        break;
      case 'status':
        const statusOrder = { 'todo': 1, 'in-progress': 2, 'review': 3, 'done': 4, 'blocked': 5 };
        aValue = statusOrder[a.status];
        bValue = statusOrder[b.status];
        break;
      case 'summary':
        aValue = a.summary.toLowerCase();
        bValue = b.summary.toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return sortedTasks;
};

export const isTaskOverdue = (task: Task): boolean => {
  const today = new Date();
  const expectedDate = new Date(task.expectedDate);
  return expectedDate < today && task.status !== 'done';
};

export const isTaskDueToday = (task: Task): boolean => {
  const today = new Date();
  const expectedDate = new Date(task.expectedDate);
  return (
    expectedDate.getDate() === today.getDate() &&
    expectedDate.getMonth() === today.getMonth() &&
    expectedDate.getFullYear() === today.getFullYear()
  );
};

export const isTaskDueThisWeek = (task: Task): boolean => {
  const today = new Date();
  const expectedDate = new Date(task.expectedDate);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  
  return expectedDate >= startOfWeek && expectedDate <= endOfWeek;
};

export const getUniqueValues = (tasks: Task[], field: keyof Task): string[] => {
  const values: string[] = [];
  
  tasks.forEach(task => {
    const value = task[field];
    
    // Handle different field types explicitly
    if (field === 'labels' && Array.isArray(value)) {
      // We know labels is string[]
      values.push(...(value as string[]));
    } else if (typeof value === 'string' && value.trim() !== '') {
      // Handle string fields
      values.push(value.trim());
    }
    // Skip other types (objects, numbers, dates, etc.)
  });
  
  return Array.from(new Set(values)).filter(Boolean).sort();
};

export const getFilterSummary = (filter: ExtendedTaskFilter): string => {
  const activeFilters = Object.entries(filter).filter(([key, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  });

  if (activeFilters.length === 0) return 'No filters applied';
  
  const filterDescriptions = activeFilters.map(([key, value]) => {
    switch (key) {
      case 'search':
        return `Search: "${value}"`;
      case 'isOverdue':
        return 'Overdue tasks';
      case 'isDueToday':
        return 'Due today';
      case 'isDueThisWeek':
        return 'Due this week';
      case 'hasVideoTutorial':
        return 'Has tutorial';
      case 'priorities':
        return `Priorities: ${(value as string[]).join(', ')}`;
      case 'labels':
        return `Labels: ${(value as string[]).join(', ')}`;
      case 'sortBy':
        return `Sorted by: ${value} (${filter.sortOrder || 'desc'})`;
      case 'sortOrder':
        return ''; // Handled with sortBy
      default:
        return `${key}: ${value}`;
    }
  }).filter(Boolean);

  return filterDescriptions.join(' • ');
}; 